import { useState, useRef, useEffect } from 'react';
import { Clock } from 'lucide-react';
import { cn } from '../lib/utils';

/**
 * TouchTimePicker — two-column digital wheel (Hour | Minute) optimised for touchscreens.
 *
 * Behaves like a normal controlled input, value is "HH:MM" (24h).
 * Designed to drop-in replace any `<input type="time">` across the app.
 *
 * Props:
 *  - value     "HH:MM"   (controlled)
 *  - onChange  (v) => void
 *  - step      minute step (default 15)
 *  - hour12    show AM/PM toggle column (default false — backend stores 24h)
 *  - minHour / maxHour clamp the hour column (default 0 / 23)
 *  - disabled  bool
 *  - placeholder, className, testId
 */
const TouchTimePicker = ({
  value = '',
  onChange,
  step = 15,
  hour12 = false,
  minHour = 0,
  maxHour = 23,
  disabled = false,
  placeholder = '--:--',
  className = '',
  testId = 'touch-time-picker',
}) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const hourRef = useRef(null);
  const minRef = useRef(null);

  const [h, m] = (() => {
    const parts = (value || '').split(':');
    if (parts.length !== 2) return [null, null];
    const hh = parseInt(parts[0], 10);
    const mm = parseInt(parts[1], 10);
    if (Number.isNaN(hh) || Number.isNaN(mm)) return [null, null];
    return [hh, mm];
  })();

  // Build hour & minute options
  const hours = [];
  for (let i = minHour; i <= maxHour; i += 1) hours.push(i);
  const minutes = [];
  for (let i = 0; i < 60; i += step) minutes.push(i);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // Auto-scroll active to centre when opened
  useEffect(() => {
    if (!open) return;
    const scrollActive = (ref) => {
      if (!ref.current) return;
      const active = ref.current.querySelector('[data-active="true"]');
      if (active) active.scrollIntoView({ block: 'center', behavior: 'instant' });
    };
    // run after paint
    setTimeout(() => { scrollActive(hourRef); scrollActive(minRef); }, 30);
  }, [open]);

  const set = (hh, mm) => {
    const out = `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
    onChange?.(out);
  };

  const displayHour = (hh) => {
    if (!hour12) return String(hh).padStart(2, '0');
    const mod = hh % 12;
    return String(mod === 0 ? 12 : mod).padStart(2, '0');
  };

  const formatted = (() => {
    if (h === null || m === null) return '';
    if (hour12) {
      const suf = h >= 12 ? 'PM' : 'AM';
      return `${displayHour(h)}:${String(m).padStart(2, '0')} ${suf}`;
    }
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  })();

  return (
    <div ref={wrapperRef} className={cn('relative inline-block w-full', className)}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(v => !v)}
        className={cn(
          'flex items-center justify-between gap-2 w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm transition focus:outline-none focus:ring-2 focus:ring-teal-300',
          disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-teal-300',
          !formatted && 'text-slate-400'
        )}
        data-testid={testId}
        data-value={value || ''}
      >
        <span className="font-mono tabular-nums tracking-wider">{formatted || placeholder}</span>
        <Clock size={14} className="text-slate-400" />
      </button>

      {open && !disabled && (
        <div
          className="absolute z-50 mt-1 left-0 bg-white rounded-xl shadow-xl border border-slate-200 p-2 select-none"
          data-testid={`${testId}-popover`}
        >
          <div className="flex gap-1">
            {/* Hour column */}
            <div
              ref={hourRef}
              className="h-44 w-14 overflow-y-auto snap-y snap-mandatory scrollbar-hide rounded-lg bg-slate-50"
              data-testid={`${testId}-hours`}
            >
              {hours.map(hh => {
                const isActive = h === hh;
                return (
                  <button
                    key={hh}
                    type="button"
                    data-active={isActive}
                    onClick={() => set(hh, m == null ? minutes[0] : m)}
                    className={cn(
                      'w-full h-11 snap-center text-center text-xl font-bold transition rounded-md tabular-nums',
                      isActive
                        ? 'bg-blue-600 text-white shadow-md ring-2 ring-blue-300 scale-100'
                        : 'text-slate-700 hover:bg-slate-100',
                    )}
                  >
                    {displayHour(hh)}
                  </button>
                );
              })}
            </div>
            {/* Minute column */}
            <div
              ref={minRef}
              className="h-44 w-14 overflow-y-auto snap-y snap-mandatory scrollbar-hide rounded-lg bg-slate-50"
              data-testid={`${testId}-minutes`}
            >
              {minutes.map(mm => {
                const isActive = m === mm;
                return (
                  <button
                    key={mm}
                    type="button"
                    data-active={isActive}
                    onClick={() => set(h == null ? hours[0] : h, mm)}
                    className={cn(
                      'w-full h-11 snap-center text-center text-xl font-bold transition rounded-md tabular-nums',
                      isActive
                        ? 'bg-blue-600 text-white shadow-md ring-2 ring-blue-300'
                        : 'text-slate-700 hover:bg-slate-100',
                    )}
                  >
                    {String(mm).padStart(2, '0')}
                  </button>
                );
              })}
            </div>
            {/* AM/PM column (only when hour12 = true) */}
            {hour12 && (
              <div className="h-44 w-14 flex flex-col gap-1 rounded-lg bg-slate-50 p-1">
                {['AM', 'PM'].map(p => {
                  const isPm = p === 'PM';
                  const currentIsPm = (h ?? 0) >= 12;
                  const active = currentIsPm === isPm;
                  return (
                    <button
                      key={p}
                      type="button"
                      onClick={() => {
                        const baseHr = h ?? hours[0];
                        const mod = baseHr % 12;
                        const newH = isPm ? mod + 12 : mod;
                        set(newH, m == null ? minutes[0] : m);
                      }}
                      className={cn(
                        'flex-1 text-base font-bold rounded-md transition',
                        active ? 'bg-blue-600 text-white shadow-md' : 'text-slate-700 hover:bg-slate-100',
                      )}
                    >{p}</button>
                  );
                })}
              </div>
            )}
          </div>
          <div className="flex items-center justify-between mt-2 px-1">
            <button
              type="button"
              className="text-[11px] text-slate-500 hover:text-slate-700"
              onClick={() => {
                const now = new Date();
                const nh = Math.min(maxHour, Math.max(minHour, now.getHours()));
                const nm = minutes.reduce((best, mm) => (Math.abs(mm - now.getMinutes()) < Math.abs(best - now.getMinutes()) ? mm : best), minutes[0]);
                set(nh, nm);
              }}
              data-testid={`${testId}-now`}
            >Now</button>
            <button
              type="button"
              className="text-[11px] font-semibold text-teal-700 hover:text-teal-800"
              onClick={() => setOpen(false)}
              data-testid={`${testId}-done`}
            >Done</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TouchTimePicker;
